import { type Channel, Socket } from "phoenix";
import { createEffect, on } from "solid-js";
import { channelPushError } from "./api";
import { token } from "./auth";
import { canonicalChannel } from "./channelKey";
import { isOffline } from "./connectivity";
import { moduleRoot } from "./moduleRoot";
import { setServerProtocol } from "./serverProtocol";
import {
  recordConnectAttempt,
  recordSocketClose,
  recordSocketError,
  recordSocketOpen,
} from "./socketHealth";

// Phoenix Channels singleton. Mirrors `auth.ts`'s module-singleton shape:
// every component that needs the live event-push surface joins via the
// helpers here; one Socket per browser tab regardless of how many call
// sites subscribe.
//
// Lazy construction: `getSocket()` builds the Socket on first call (i.e.
// the first time something tries to join). Until then nothing connects
// — RequireAuth bounces unauthenticated visitors to /login before any
// channel-needing component renders, so the lazy path means we don't
// even try to open a WS without a bearer.
//
// Token-driven lifecycle: phoenix.js evaluates the Socket's `params`
// callback only at WS-handshake time, so a live connection stays
// pinned to whatever bearer the handshake captured. Three transitions
// matter — login (null → t: connect), logout (t → null: disconnect),
// and rotation (a → b, both non-null and distinct: disconnect+connect
// to flush the new bearer onto the next handshake). Phase 5
// token-refresh and admin-driven re-issue both ride this path; without
// the rotation arm they would silently route under the stale identity.
// `on(token, fn)` gives us the prev value the rotation check needs;
// createRoot anchors the effect since module-level effects need an
// owner.
//
// Rotation side-effect — and its NON-obvious rejoin contract: rotation
// REBUILDS the Socket (`_socket = null`, then a fresh `getSocket()`), it
// does NOT reconnect the same instance. phoenix.js's per-Channel
// auto-rejoin only fires on the SAME Socket's `connect()` (it re-runs the
// channels it still holds in `socket.channels[]`); a brand-new instance
// starts with ZERO channels, so nothing auto-rejoins. Every topic must be
// re-joined by the effect that owns it — the token-tracking join effects
// in `subscribe.ts` (per-channel/DM/query) and `userTopic.ts` (user
// topic) each re-run on the token change and re-`joinUser`/`joinChannel`
// on the rebuilt socket. A stale "phoenix auto-rejoins on connect"
// mental model here is exactly what let #364 (cicchetto S1) ship: the
// user-topic effect early-returned on the unchanged identity and never
// re-joined the rebuilt socket. Phase 5 telemetry should observe the
// rejoin volume on rotation events.
//
// Topic vocabulary mirrors `Grappa.PubSub.Topic` exactly. Don't
// reformat segment separators or re-encode identifiers — the server's
// `Topic.parse/1` is the authority and accepts these byte-for-byte.

let _socket: Socket | null = null;

// #447/#1379 — the wire protocol version cicchetto speaks, declared to the
// server on the WS upgrade so the handshake can refuse a bundle that is too
// old (426 Upgrade Required, `UserSocket.check_protocol_version/1`).
//
// Declaring is opt-in on the client side and cic had opted OUT by omission: a
// socket with no `client_proto` is treated as CURRENT whatever the bundle
// actually is. That default is wrong for cic specifically — it is a
// service-worker-cached PWA, i.e. the client most able to be arbitrarily stale
// against a freshly deployed BEAM, and `--cic`-only vs server-only deploys are
// routine here. The refusal exists for exactly this client; declaring arms it.
//
// The number is the CONTRACT version (`Grappa.Protocol.version/0`), NOT the
// software release in `<meta cicchetto-version>`. `Grappa.Protocol`'s floor
// and this constant are pinned against each other server-side in
// `test/grappa/protocol_test.exs`; raising `min_version/0` past this number
// means every un-updated cic bundle is refused, so the two move together.
//
// #1393d — 1 → 2. The old rule written here ("bump it only when cic is
// changed to speak a new protocol the additive-only rule cannot express — a
// new event kind or field is free and never moves it") is gone with its
// server-side twin: the wire version now moves on EVERY shape change.
//
// 2 → 9. Two reasons and the first alone is sufficient. (1) The number is the
// CONTRACT version, and the server has been at 9 since the #1280 profile
// fields landed; 2 had simply stopped tracking it across the intervening
// bumps, which is why every boot logged `protocol mismatch: this bundle
// speaks 2, the server speaks 9` — a true statement about a stale constant,
// not about a real incompatibility. (2) `MIN_SERVER_PROTOCOL_VERSION` had to
// rise to 9 to stop lying about the credential shape this bundle requires
// (see its own note), and `MIN_SERVER <= CLIENT` is pinned in
// `serverProtocol.test.ts` — a bundle requiring more than it speaks would
// refuse every server that accepts it. The pair moves together or neither
// moves.
//
// 9 → 13. The same defect as 2 → 9, recurring for the same reason: nothing
// pinned this number to `Grappa.Protocol.version/0` — only to its FLOOR,
// which is 1 and which everything satisfies. In the seven days after 9
// landed the server bumped four times (10, the server_pass route; 11, the
// upload-confirm settings door; 12, the ISON presence fallback; 13, the
// stale_code_path refusal) and this constant did not move, so every boot of
// every current bundle logged `protocol mismatch: this bundle speaks 9, the
// server speaks 11` against production. A true statement about a stale
// constant, again — and this time it cost someone real work: a tester
// narrowing an unrelated report read it in the console and offered it as the
// likely cause.
//
// The bundle already SPOKE 13 and merely declared 9. `wireSchema.ts` carries
// v12's `presence_changed.source: "ison"` and v13's `stale_code_path`
// refusal token; the number was the only thing lagging.
//
// Measured, because the shape of the recurrence is the argument for the new
// pin rather than for more diligence: 12 bumps of `@protocol_version`
// against 3 writes of this constant, two of which were catch-ups (2 → 9
// swallowed seven bumps at once). Since cic first declared a version the two
// have been equal for roughly 8 days out of 22 — the stale value is the
// NORMAL state of this file, not an accident that better attention would
// have avoided.
//
// Why it is not cosmetic, and why the cure is a pin and not a habit:
// `noteServerProtocol` below warns on ANY inequality and its only silence is
// exact equality, so a lagging constant fires "protocol mismatch" on every
// healthy boot of a deploy whose bundle and BEAM came from the SAME commit.
// That is the one signal for a service-worker-cached PWA skewed against the
// BEAM (see its own note), and a signal that fires on every healthy boot
// cannot carry that meaning. `protocol_test.exs` now pins
// `cic_protocol_version() == Protocol.version()`, so the next bump that
// forgets this file is red at the commit that forgets it, and an inequality
// at RUNTIME means what it is supposed to mean: the two artefacts came from
// different commits.
//
// Raising THIS one is safe in the direction that can bite: the handshake
// refuses a client BELOW `Grappa.Protocol.min_version/0`, which is 1, and
// never one above. `protocol_test.exs` pins that from the other side
// (`cic_protocol_version() >= Protocol.min_version()`) and, since #1973, the
// equality against `version/0` as well.
//
// This is what cic SPEAKS. What it REQUIRES of the server is a separate
// constant in `serverProtocol.ts` (`MIN_SERVER_PROTOCOL_VERSION`), and the
// two are deliberately not the same number — a later bundle may speak v5
// and still cope with a v2 server. They coincided until this bump and no
// longer do: this bundle speaks 13 and still serves a v9 server. That gap is
// the two axes behaving as designed, not drift to be tidied away — raising
// the floor is the #1654 question and is not answered here.
//
// 13 → 14 (#162): the first bump under the #1973 pin, and it worked as
// designed — `protocol_test.exs` went red at the commit that moved
// `@protocol_version` (the `invalid_mask` token) without this line, so the
// two moved together instead of this file lagging for another three weeks.
//
// 14 → 15 (#2029): the second, and the pin earned its keep twice over. The
// `display_prefs` object grew a fifth key (`strip_formatting`) and `wire_pin
// --check` could NOT see it — its digest does not span hand-written
// `*_json.ex` views, so it answered "wire shape and protocol 14 agree" at rc=0
// on a real shape change. `protocol_test.exs` is what went red for the missing
// half of the bump, which makes it the ONLY automatic guard on this pair for
// any payload the codegen does not cover. `MIN_SERVER_PROTOCOL_VERSION` stays
// at 9, correctly: the new key is absent-tolerant in both directions, so this
// bundle still serves a v9 server — the two axes behaving as designed.
//
// 15 → 16 (#2037): the third, and the third time `wire_pin --check` was blind
// for the same reason — `GrappaWeb.MessagesJSON` is not a codegen source
// either. The gap-probe response grew `messages` + `events` beside `count`, so
// the far-behind bar renders the same quantity the sidebar's bold pill does
// instead of a raw row count nothing else on screen shared.
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: `countMessagesAfter` falls back to
// `{messages: count, events: 0}` when the pair is absent, which is exactly the
// pre-#2037 number in the pre-#2037 place, so this bundle still serves an
// older server.
//
// 17 (issue 2046) — the channel-directory `status` union is re-spelled:
// `empty` and `refreshing` are gone, `no_results`, `unknown` and `loading`
// take their place. Unlike every bump before it this one is NOT additive, so
// the direction of the break is worth naming: this bundle cannot read a
// pre-17 server's directory page either — `wireSchema` rejects `empty` just
// as an old bundle rejects `loading`. `MIN_SERVER_PROTOCOL_VERSION` still
// stays at 9, deliberately: raising it would refuse the whole socket over one
// broken pane, and the two ship together anyway.
//
// 18 (issue 1480) — `notification_prefs` grows `notification_sound`, the
// in-app beep preset. Additive, and the FOURTH bump `wire_pin --check` cannot
// see: this one rides `UserSettingsJSON`, whose spec names the prefs type
// remotely, so growing the type moves no digest byte.
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: this bundle falls back to `none`
// when the server does not send the key, which is the pre-#1480 behaviour in
// the pre-#1480 place — silence, since the default is silence — so it still
// serves an older server. The direction that would hurt is the other one, and
// the server closes it: it treats an ABSENT key on the PUT as unchanged, so an
// old bundle saving any other pref cannot mute a subject who opted in.
//
// 19 (issue 2089) — two new user-topic event kinds for DCC RECEIVE:
// `dcc_offer` (a peer offered a file and the bouncer is HOLDING it, awaiting
// explicit consent) and `dcc_offer_resolved` (it left the held set —
// accepted, refused or expired). Additive, and unlike 18 this one IS visible
// to `wire_pin --check`: both are named payload types on
// `Grappa.Session.Wire`, so the codegen renders them and the digest moves.
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: a pre-19 server simply never
// sends these kinds, and a bundle that never receives a `dcc_offer` shows no
// banner — which is the pre-#2089 behaviour in the pre-#2089 place, so this
// bundle still serves an older server. The other direction is closed too: an
// old bundle ignores both kinds (unknown-is-never-fatal) and never accepts an
// offer, so the server holds it until it expires rather than acting on a
// consent nobody gave.
//
// 20 (issue 2089, same slice as 19) — one new REST error token, `not_held`,
// answered 404 by the four DCC consent doors when the offer id names nothing
// in the session's held set: it expired, it was already answered, or it was
// never ours. A member added to a CLOSED SET the codegen renders, so the wire
// shape moved and the number bumps even though nothing was taken away —
// additive still bumps (#1393d), because additivity describes what the SERVER
// emits and says nothing about what a CLIENT requires.
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: a pre-20 server never answers
// `not_held`, and this bundle treats an unfamiliar token the way it always
// has, so it still serves an older server.
// 21 (issue 2127) — the accepted-DCC-file door MOVES rather than gaining a
// field: `GET /networks/:network_id/dcc_files/:slug` behind `:authn` becomes
// `GET /dcc_files/:slug[.ext]` at top level with no auth, the same public
// surface as `/uploads/:slug`, with the 26-char base32 slug as the access
// token. A path was TAKEN AWAY, so unlike the additive bumps above the break
// runs in BOTH directions.
//
// This bundle needs no code change for it. cic never CONSTRUCTS that URL —
// the server mints it absolute into the delivery row's body and `linkify.ts`
// renders whatever it is handed — so the move is invisible here except as
// this number. (The SW navigation denylist gained `/^\/dcc_files/` in the
// same change, for the reason `/uploads` is on it: an absolute link tapped
// out of scrollback is a top-level navigation, and without the entry the SW
// serves the SPA shell instead of the bytes.)
//
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: a pre-21 server mints the OLD
// relative path, which this bundle renders as plain text exactly as it
// always did, so an older server is still served.
// 22 (issue 2143) — the per-network DCC auto-accept opt-in gets a REST door:
// `GET`/`PUT /networks/:network_id/dcc-auto-accept`, carrying one `enabled`
// boolean. Purely additive, and additive still bumps (#1393d).
//
// This bundle needs no code change for it, and in this cut has none: the
// switch is settable over REST and nothing else, because whether a control
// ships is a product ruling that was escalated rather than decided in the
// slice. The number moves anyway — it says what cic SPEAKS, and the server
// speaks v22, so an inequality here would mean the two artefacts came from
// different commits, which is the one thing `noteServerProtocol` exists to
// tell a tester.
//
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: this bundle requires nothing
// from v22, so it still serves an older server.
//
// 23 (issue 2150) — two new user-topic push kinds for the remembered leave
// reasons: `quit_part_reason_changed` and `auto_away_reason_changed`. Each
// carries one `string | null` field named after its key, and `null` is a
// VALUE — it is how "I cleared it" travels — so the key is always present.
//
// This bundle DOES change for it: `userTopic.ts` grows an arm per kind, and
// each arm validates its payload against the generated schema before it
// touches a store. That is what makes the bump load-bearing rather than
// bookkeeping — a v23 bundle mirrors state a pre-23 server never pushes.
//
// 22 is not ours and the gap is deliberate: #2143 claimed it concurrently
// and landed first, so this branch took 23 and sits one above. The number
// must stay MONOTONIC on main — a client reading `server >= N` as "has
// everything N had" is entitled to, and walking it backwards makes that
// reading false forever (#1393d, the floor that lies).
//
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9: the two arms are additive
// pushes an older server simply never sends, and an absent push leaves the
// stores at the value the REST read already gave them, so this bundle is
// still served by an older server.
//
// 34 (issue 1365 leg 2) — `dm_conversation_id` on every scrollback row and
// every `query_windows_list` entry, beside the nick. This bundle does not
// read it yet (leg 3 does), so the number moves because the server speaks
// v34 and the pair must come from one commit, as at v22. Both keys are
// generated OPTIONAL, so this bundle still validates a pre-34 server's
// pages and `MIN_SERVER_PROTOCOL_VERSION` stays at 9.
//
// 35 (issue 2333) — `id` and `dm_with` on every `mentions_bundle` row: the
// message to scroll to on a tap, and the peer window of an inbound DM. Both
// keys are generated OPTIONAL, so this bundle still validates a pre-35
// server's away bundle (the tap then only switches windows) and
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9.
//
// 36 (issue 1365) — `dm_with` on every scrollback row, the DM discriminator
// a nick change never rewrites (`pushTriggers.shouldNotify` reads it).
// Generated OPTIONAL, so this bundle still validates a pre-36 server's rows;
// `MIN_SERVER_PROTOCOL_VERSION` stays at 9 (issue 1365's Q2: the floor does
// not move).
//
// 37 (issue 1365) — `dm_conversation_id` is taken back off both shapes 34
// put it on. This bundle never read it; its generated schema simply stops
// declaring the key. `MIN_SERVER_PROTOCOL_VERSION` stays at 9: a 34-36
// server still sends the key, and an undeclared key is dropped, never
// rejected.
//
// 38 (issue 2347) — `GET`/`PUT /me/settings/ban-mask-form`, the default ban
// type `/kb` and Kickban compose. REST only, no push. A pre-38 server 404s
// the read, and the bundle falls back to `"host"` — the mask `/kb` always
// sent — so `MIN_SERVER_PROTOCOL_VERSION` stays at 9.
//
// 40 (issue 2372) — time cursors on `GET …/messages` (`before_time` /
// `after_time` / `around_time`, epoch ms). REQUEST params only; this bundle
// pages by id and sends none of them, so `MIN_SERVER_PROTOCOL_VERSION`
// stays at 9.
export const CLIENT_PROTOCOL_VERSION = 40;

// #193 — force the correct WS scheme from the page origin, absolutely.
//
// Passing the relative `"/socket"` to phoenix.js makes phoenix derive
// the scheme from `location.protocol` at connect time — normally fine.
// But the bug: on an https-served PWA a subset of reconnects went out
// over `ws://` (plaintext :80), the :80 vhost answered `301 https://…`,
// and a WS handshake does NOT follow redirects → the upgrade failed and
// the client backoff-looped on the splash after a BEAM restart (34 such
// `GET /socket/websocket → 301` lines across 5 real clients).
//
// The durable fix is to construct an ABSOLUTE endpoint with the scheme
// pinned by OUR code, not phoenix's derivation: on any `https:` origin
// the scheme is unconditionally `wss:`; only a genuinely plaintext
// `http:` origin (dev over http, LAN dev) gets `ws:`. Because the
// running bundle recomputes this from `location.protocol` at every
// connect(), a stale service-worker-cached bundle can't pin a `ws://`
// endpoint either (belt to the SW's own suspenders — the SW denylists
// `/socket` and never caches the WS upgrade; VitePWA `autoUpdate` swaps
// the bundle on the next load regardless). SSR / no-DOM → relative
// `/socket` (jsdom unit tests, no location to read).
//
// The server-side `:80` mitigation (proxy the upgrade instead of 301'ing
// it) is an m42 nginx HOST-config change, out of scope for this fix —
// noted for the orchestrator. This client change stands on its own.
//
// This value must stay a bare ORIGIN + PATH with no query string. phoenix.js's
// Socket constructor appends the transport by string concatenation —
// `this.endPoint = `${endPoint}/websocket`` — before `endPointURL()` appends
// any params, so a `?` here lands on the wrong side of that join: the dialled
// path collapses back to `/socket` (which the `socket "/socket"` mount does
// not serve) and `/websocket` is swallowed into a param value. Anything that
// belongs in the query goes through the Socket's `params` (see `getSocket`),
// which is the only hook `Ajax.appendParams` reaches AFTER the transport.
export function socketEndpoint(loc?: Location | { protocol: string; host: string }): string {
  const l = loc ?? (typeof location !== "undefined" ? location : undefined);
  if (!l) return "/socket";
  const scheme = l.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${l.host}/socket`;
}

// Module-level reference to the joined user-level channel. Set by
// `joinUser` so `notifyClientClosing` can push the `client_closing`
// event on pagehide / beforeunload without passing the channel reference
// around to every call site.
let _userChannel: Channel | null = null;

function getSocket(): Socket {
  if (_socket === null) {
    _socket = new Socket(socketEndpoint(), {
      // #95 — the bearer rides the `Sec-WebSocket-Protocol` subprotocol
      // (`authToken`) ONLY, so it is entirely OFF the WS upgrade URL
      // (`?token=…` was pre-redaction visible in access logs). phoenix.js
      // sends it as `base64url.bearer.phx.<token>`; the server reads
      // `connect_info.auth_token`.
      //
      // We deliberately do NOT also pass `params: {token}`: phoenix.js's
      // `endPointURL()` appends `params()` to the query string, so
      // keeping the token there would defeat the entire point of this
      // change (the token would still sit in the URL). The server
      // (`UserSocket.connect/3`) reads the bearer ONLY from the
      // subprotocol — #202 dropped the legacy `params["token"]`
      // query-string fallback that once accepted OLD bundles mid-cold-
      // deploy, so a bearer in the URL is now ignored entirely.
      //
      // `authToken` is captured ONCE at construction (unlike a `params`
      // callback, which phoenix re-evaluates every handshake), so a fresh
      // token must REBUILD the socket — the token-effect below nulls
      // `_socket` on both logout AND rotation so the next `getSocket()`
      // captures the current bearer. Do NOT switch rotation to a
      // reconnect: that would replay the stale ctor-time `authToken`.
      authToken: token() ?? "",
      // #1379 — the protocol declaration the 426 gate reads
      // (`UserSocket.check_protocol_version/1`). It rides `params`, i.e. the
      // query string, precisely where the bearer above must NOT: the version
      // is public discovery data (also served unauth at `GET /api/config`),
      // so the #95/#202 off-the-URL rule — which is about the secret bearer —
      // does not reach it. Two orthogonal channels, no collision.
      //
      // `params` rather than the endpoint string because phoenix glues
      // `/websocket` onto the endpoint by concatenation and only params are
      // appended after that (see `socketEndpoint`). Unlike `authToken`, this
      // is a constant, so its re-evaluation on every handshake is a no-op and
      // the ctor-capture caveat above does not apply.
      params: { client_proto: CLIENT_PROTOCOL_VERSION },
    });
    // SocketHealth wiring — single install at construction time so the
    // banner reflects every transition, including silent retry loops
    // (e.g. server's check_origin rejecting the browser Origin).
    // phoenix.js's onError fires per WS open attempt that fails;
    // onClose fires with a CloseEvent we can read code/reason from.
    _socket.onOpen(() => recordSocketOpen());
    _socket.onError(() => recordSocketError());
    _socket.onClose((closeEvent: CloseEvent | undefined) => recordSocketClose(closeEvent));
  }
  return _socket;
}

moduleRoot(() => {
  createEffect(
    on(token, (t, prev) => {
      if (t === null) {
        // Logout — drop the socket AND its module-level reference so a
        // subsequent login rebuilds from scratch. Pre-fix (2026-05-27)
        // we only called `_socket.disconnect()` but kept the instance;
        // the next login's `getSocket()` returned the disconnected
        // instance and its internal state (channels, refs, params
        // closure) carried the old session. phoenix.js's `connect()`
        // on a disconnected instance does NOT re-evaluate `params()`
        // reliably across the disconnect-reconnect boundary, so the
        // new bearer never landed on the handshake and the WS never
        // came back up after a visitor logout+relogin. Symptom:
        // network HTTP shows fresh POST /auth/login + GET /networks,
        // but the BEAM log shows zero `CONNECTED TO GrappaWeb.UserSocket`
        // and zero JOINED grappa:user:... events for the new visitor id.
        //
        // #364 bucket B — disconnect UNCONDITIONALLY, not gated on
        // `isConnected()`. A socket mid-backoff (WS torn after a BEAM
        // restart / network blip, or a handshake that never completed) is
        // NOT `isConnected()`, yet phoenix keeps a live `reconnectTimer`
        // firing `connect()` under the STALE ctor-time `authToken`. Gating
        // the disconnect on `isConnected()` skipped the teardown and then
        // `_socket = null` ORPHANED that instance — its reconnectTimer kept
        // reconnecting with the old bearer, unstoppable because the
        // reference was gone (zombie loop + stale identity). `disconnect()`
        // is the app-callable that resets phoenix's reconnectTimer while the
        // WS is down (as haltForOffline/kickReconnect already rely on) and is
        // a safe no-op on a non-open socket, so it MUST run before dropping
        // the reference.
        _socket?.disconnect();
        _socket = null;
        _userChannel = null;
        return;
      }
      const s = getSocket();
      if (prev != null && prev !== t) {
        // Token rotation (Phase 5 refresh / admin re-issue). #95: the
        // bearer now rides the `authToken` subprotocol, which phoenix
        // captures ONCE at construction — a plain disconnect+reconnect
        // would replay the STALE ctor-time token. So rebuild: drop the
        // instance (like logout) and let the next getSocket() capture
        // the rotated bearer, then connect. The query-string `params`
        // callback would refresh on its own, but the server prefers the
        // subprotocol, so the subprotocol MUST carry the fresh token —
        // hence the rebuild, not a reconnect.
        //
        // #364 bucket B — disconnect UNCONDITIONALLY (same reason as the
        // logout arm above): a rotation that lands while the old socket is
        // mid-backoff (not `isConnected()`) would otherwise skip the
        // teardown and orphan an instance whose reconnectTimer keeps
        // replaying the stale bearer. `disconnect()` resets that timer and
        // is a safe no-op on a non-open socket.
        s.disconnect();
        _socket = null;
        _userChannel = null;
        // #1061 — through the gated door. A rotation that lands while the
        // device is offline must not be the thing that starts the ladder; the
        // rebuilt socket opens on the next `online` kick with the fresh bearer
        // already captured at construction.
        connectUnlessOffline(getSocket());
        return;
      }
      // #1061 — the COLD-START arm, and the second half of defect 1: a boot
      // that begins offline never sees an `offline` event (it already fired,
      // or never fired at all), so `haltForOffline` cannot save it. Only the
      // door's own check can.
      if (!s.isConnected()) connectUnlessOffline(s);
    }),
  );
});

// #119 (vjt refinement, 2026-07-04) — connectivity-driven reconnect kick.
//
// phoenix.js's Socket ALREADY auto-reconnects natively: on an unexpected
// close/error it schedules `connect()` via its internal `reconnectTimer`
// using the default `reconnectAfterMs` backoff
// ([10,50,100,150,200,250,500,1000,2000] then a 5000ms steady state), and
// every joined Channel auto-rejoins on the new socket. We do NOT construct the
// Socket with a `reconnectAfterMs` override, so that default backoff is live.
// None of that is reimplemented here.
//
// The DELTA phoenix does not give: it never listens to the browser's
// `online` / `offline` events. That leaves two gaps:
//   * On `offline`, phoenix keeps firing futile `connect()` attempts against a
//     demonstrably dead network, ratcheting its backoff toward the 5s ceiling
//     — so when connectivity returns the next native retry can be up to ~5s
//     away.
//   * On `online`, nothing signals "retry NOW"; the socket waits out whatever
//     backoff delay was pending.
// So we add ONLY the two window listeners below: `offline` → `disconnect()`
// (halt the futile retries + reset phoenix's backoff timer); `online` →
// `disconnect()` + `connect()` to force an IMMEDIATE reconnect (phoenix's own
// connect path re-evaluates `params()` and auto-rejoins every channel — we add
// only the "don't wait out the backoff" kick). Both no-op when no socket has
// been built yet (logged out / lazy pre-join).
//
// Structural subset of phoenix's `Socket` — lets the kick be unit-tested with
// a fake, without a live WS server.
export interface ReconnectableSocket {
  isConnected(): boolean;
  connect(): void;
  disconnect(callback?: () => void, code?: number, reason?: string): void;
}

// #1061 — THE ONE DOOR to phoenix's `connect()`. Every production open goes
// through here; nothing else in this module may call `s.connect()`.
//
// `navigator.onLine === false` is the one direction the browser guarantees:
// false means there is demonstrably no network (true means only "maybe"). So
// suppressing on false can never suppress a connect that would have worked,
// while NOT suppressing it starts phoenix's whole backoff ladder
// ([10,50,100,150,200,250,500,1000,2000] then 5s steady) against a network we
// have been told is dead.
//
// WHY THE GUARD LIVES INSIDE THE DOOR AND NOT AT THE CALL SITES: that is the
// entire #1061 defect 1. `haltForOffline` stops the ladder on `offline`, but
// the `offline` event fires ONCE — so a single caller that forgets the check
// re-arms the ladder permanently, with no second event left to halt it. A
// guard three callers must remember is a guard that will be forgotten; a door
// that cannot be walked around is not.
export function connectUnlessOffline(s: ReconnectableSocket | null): void {
  if (s === null) return;
  if (isOffline()) return;
  recordConnectAttempt();
  s.connect();
}

export function kickReconnect(s: ReconnectableSocket | null): void {
  if (s === null) return;
  // #1061 — bail BEFORE the disconnect, not just before the connect. On a
  // browser that still reports a live socket while `onLine` is false, tearing
  // it down would trade a possibly-working connection for a suppressed
  // reconnect: strictly worse than doing nothing, which is what an offline
  // device has asked us to do.
  if (isOffline()) return;
  if (s.isConnected()) return; // already up — don't tear a healthy socket
  // Force an immediate reconnect: disconnect() cancels any pending native
  // backoff timer and tears the half-open conn, so connect() opens NOW.
  s.disconnect();
  connectUnlessOffline(s);
}

export function haltForOffline(s: ReconnectableSocket | null): void {
  if (s === null) return;
  // disconnect() stops phoenix's futile reconnect attempts on a dead network
  // and resets its backoff timer. We keep the same instance + its joined
  // channels so kickReconnect() re-opens and phoenix auto-rejoins on `online`.
  s.disconnect();
}

if (typeof window !== "undefined") {
  // #1061 — LISTENER ORDER IS LOAD-BEARING, and it is guaranteed rather than
  // hoped for. `kickReconnect` now reads `isOffline()`, so on the `online`
  // event connectivity's own listener MUST have flipped the signal before this
  // one runs, or the kick would suppress itself on the very event that means
  // "you may dial again". ESM evaluates a module's dependencies before its
  // body, and this module imports `./connectivity` — so connectivity's
  // listener is always registered first, and same-target listeners fire in
  // registration order. Do not move the offline predicate to a lazily-created
  // module, and do not register these listeners from an importer.
  window.addEventListener("online", () => kickReconnect(_socket));
  window.addEventListener("offline", () => haltForOffline(_socket));
  // #254 — iOS PWA wake reconnect kick. On background→foreground iOS can
  // silently tear the WS with NO `online` event (the network was never
  // "offline" from the browser's POV), so the handler above never fires and
  // phoenix may still be waiting out its reconnect backoff. A
  // visibilitychange→visible kick forces an immediate reconnect so an own
  // message sent right after wake has a live subscription for its echo — the
  // echo stays the sole render path (no optimistic local render). Twin of the
  // `online` handler; `kickReconnect` no-ops on an already-healthy socket.
  //
  // #1061 defect 2 — this is a SECOND visibilitychange handler: phoenix 1.8.9
  // registers its own (phoenix.mjs:1113) which, on visible, runs
  // `if (!isConnected() && !closeWasClean) teardown(() => connect())`. The
  // difference that matters is the `closeWasClean` precondition. phoenix's
  // `disconnect()` SETS `closeWasClean = true` (phoenix.mjs:1223), so after
  // `haltForOffline()` phoenix's own handler correctly declines to reconnect —
  // ours, being unconditional, was the one that re-armed the ladder. The
  // `isOffline()` guard now inside `kickReconnect` closes that; whether the
  // duplicate handler should exist AT ALL on 1.8.9 is a design call recorded
  // on #1061, deliberately not taken here.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") kickReconnect(_socket);
    });
  }
}

// #447/#1379 — the user topic is the first topic cic joins, so its join reply
// is the initial WS payload and carries `protocol_version`: the contract
// version the SERVER speaks.
//
// #1393d — WHAT THIS COMMENT USED TO SAY, AND WHY IT IS NO LONGER TRUE.
//
// It said the number was "good for diagnosis, and only diagnosis", because
// "a newer client tolerates an older server — so there is nothing here to
// gate on and no UX to raise", and it reduced the whole fact to a one-shot
// `console.warn`. Both halves of that have since failed, in opposite ways,
// and it is worth recording which:
//
//   * The PREMISE was repealed. A newer client tolerated an older server
//     only because cic INVENTED every field it did not receive. #1393d
//     stopped: a payload missing a required field is dropped whole. So a
//     newer client no longer tolerates an older server, and a `--cic`-only
//     deploy — bundle ships, BEAM is not restarted, a routine class here —
//     silently kills /banlist, the /mode toggles and the invited tab.
//
//   * The MECHANISM this comment implied would not have worked either, and
//     that is the part worth measuring rather than asserting. Comparing
//     against `protocol_version` was useless while the additive-only rule
//     stood: measured on this repo, the number sat at `1` from #447
//     (2026-07-27) through five additive field adds — `recoverable`,
//     `inviter`, `list_modes_queryable`, `chantypes`, `prefix_order` — every
//     one of which cic came to require. A floor of `1` is satisfied by
//     exactly the servers that break.
//
// What makes it actionable is not this file: it is vjt's ruling (2026-08-21)
// that the server bumps `Grappa.Protocol.version/0` on EVERY wire-shape
// change from now on, additive included. Only then does "server >= N" mean
// "the server has everything N had", and only then is a floor honest.
//
// So the number is now FED to `serverProtocol.ts`, which owns the floor and
// the comparison; this function's whole job is to hand it over. The
// `console.warn` stays for a mismatch in EITHER direction (a server AHEAD of
// this bundle is still nothing to gate on — that direction really is
// additive, and additive really is tolerated) because on a
// service-worker-cached PWA a bundle/BEAM skew is the single most likely
// explanation for otherwise-inexplicable behaviour, and it is invisible from
// every other signal.
//
// Logged once per bundle load, not once per join: joinUser's "ok" hook
// re-fires on every phoenix auto-rejoin, and a PWA re-joins on every network
// blip. The FLOOR is not rate-limited that way — it is a signal, not a log,
// and setting it twice to the same value is a no-op.
let _serverProtocolLogged = false;

function noteServerProtocol(reply: unknown): void {
  const server = (reply as { protocol_version?: unknown } | null | undefined)?.protocol_version;
  if (typeof server !== "number") return;
  setServerProtocol(server);
  if (server === CLIENT_PROTOCOL_VERSION) return;
  if (_serverProtocolLogged) return;
  _serverProtocolLogged = true;
  console.warn(
    `[grappa] protocol mismatch: this bundle speaks ${CLIENT_PROTOCOL_VERSION}, the server speaks ${server}`,
  );
}

// joinUser + joinChannel mirror Topic.user/1 + Topic.channel/3 from the
// server. `joinNetwork` (per-(user, network) shape) is reserved
// infrastructure on the server side but has no cicchetto consumer yet —
// add it back when a real consumer (presence per network, MOTD on the
// per-user topic, etc.) needs it.
export function joinUser(userName: string, onJoinOk?: (reply: unknown) => void): Channel {
  const topic = `grappa:user:${userName}`;
  const ch = getSocket().channel(topic);
  ch.join()
    .receive("ok", (reply: unknown) => {
      noteServerProtocol(reply);
      if (onJoinOk) onJoinOk(reply);
      // #182 — report the current PWA visibility right after the join
      // (and on every auto-rejoin, since phoenix re-fires this "ok" hook
      // on reconnect). The server's WSPresence defaults a fresh transport
      // pid to :hidden, so this initial report is what lets a foregrounded
      // tab suppress push + cancel auto-away without a visibilitychange.
      reportVisibility();
    })
    .receive("error", (err: unknown) => {
      console.error("[grappa] channel join failed", topic, err);
    })
    .receive("timeout", () => {
      console.error("[grappa] channel join timed out", topic);
    });
  // Track the user-level channel for the pagehide immediate-away hint (S3.3)
  // and the visibility reporter (#182).
  _userChannel = ch;
  return ch;
}

// #1769 — the join params a per-channel topic accepts. One key today.
//
// `presence: false` asks the server not to push peer join/part/quit for this
// channel (the server half of #1680's pause). OMITTING the key is the
// default and means everything — which is the whole compatibility argument
// for the shape, so this type must never grow a required member.
//
// The server reads it ONCE, in `GrappaWeb.GrappaChannel.join/3`. Changing
// your mind is a re-join, not a push; `subscribe.ts:rejoinChannelWithPresence`
// is the one caller that does that.
export interface ChannelJoinParams {
  presence?: boolean;
}

export function joinChannel(
  userName: string,
  networkSlug: string,
  channelName: string,
  onJoinOk: ((reply: unknown) => void) | undefined,
  // REQUIRED, not defaulted: a default would let a new call site acquire the
  // presence decision without naming it, and every existing site passing an
  // explicit `{}` is what makes "who asks for suppression?" greppable.
  params: ChannelJoinParams,
): Channel {
  // UX-4 bucket A — canonicalise channel-shape segment so cic joins
  // the same Phoenix topic the server broadcasts on. Server-side
  // `Grappa.PubSub.Topic.channel/3` canonicalises at build time; if
  // cic subscribed to `#Chan` while server emits on `#chan`, the
  // fastlane fan-out would skip this socket entirely. Nicks (DM
  // windows) pass through unchanged.
  const topic = `grappa:user:${userName}/network:${networkSlug}/channel:${canonicalChannel(channelName)}`;
  // Params are passed as an OBJECT rather than a thunk deliberately. phoenix.js
  // re-evaluates a thunk on every auto-rejoin, which sounds like the safer
  // choice and is not: it would let a socket-level reconnect silently change
  // what this Channel asked for, so the object it joined with would stop
  // describing it. A pause/resume transition re-joins explicitly instead.
  const ch = getSocket().channel(topic, params);
  // Surface server-side join failures to the console + Phase 5
  // telemetry hook (the `unknown topic` and `forbidden` shapes the
  // server returns from `GrappaChannel.join/3` would otherwise vanish
  // silently). `timeout` is the phoenix.js retry-budget exhaustion
  // shape; logging it lets a stuck channel show up in operator
  // browser-console output during diagnosis.
  //
  // `onJoinOk` (message-replay-on-reconnect cluster, 2026-05-12) fires
  // on EVERY successful join — both the initial join and every
  // auto-rejoin after a socket disconnect. phoenix.js's
  // `Push.resend()` does NOT clear the `recHooks` list (only resets
  // ref/refEvent/receivedResp/sent), so a `.receive("ok", cb)`
  // registered once at first join keeps firing on subsequent rejoins.
  // The cic backfill flow uses this fan-in to detect "this is a
  // re-join" and fetch the rows that arrived during the WS gap.
  //
  // The join `reply` carries the per-channel cursor for CP29 R-4 read
  // state (`%{read_cursor: <id_or_nil>}`); subscribers narrow it via
  // `readCursor.ts:applyJoinReply/3`.
  ch.join()
    .receive("ok", (reply: unknown) => {
      if (onJoinOk) onJoinOk(reply);
    })
    .receive("error", (err: unknown) => {
      console.error("[grappa] channel join failed", topic, err);
    })
    .receive("timeout", () => {
      console.error("[grappa] channel join timed out", topic);
    });
  return ch;
}

// M-11 — join the admin-events channel (`grappa:admin:events`).
// Authz is `is_admin: true` server-side; non-admin sockets get
// `{:error, %{error: "forbidden"}}` and the .receive("error")
// arm fires. AdminPane.tsx gates the join on `me.is_admin`, so
// the forbidden branch is a defense-in-depth fallback rather
// than the expected path.
export function joinAdminEvents(): Channel {
  const topic = "grappa:admin:events";
  const ch = getSocket().channel(topic);
  ch.join()
    .receive("error", (err: unknown) => {
      console.error("[grappa] admin-events join failed", topic, err);
    })
    .receive("timeout", () => {
      console.error("[grappa] admin-events join timed out", topic);
    });
  return ch;
}

// S3.3 — pagehide immediate-away hint.
//
// Pushes `client_closing` over the active user-level channel so the
// server's WSPresence marks the closing tab hidden and fires
// `:ws_all_hidden` immediately — bypassing the 30s auto-away debounce —
// if it was the last visible device. No-op if no user channel has been
// joined yet (which can happen if the page unloads before `joinUser`
// completes).
//
// The push is fire-and-forget: `pagehide` / `beforeunload` give no
// time to await a reply. The server handles idempotency — the pid DOWN
// from the real socket close is a no-op if client_closing already
// fired the notification.
export function notifyClientClosing(): void {
  if (_userChannel === null) return;
  _userChannel.push("client_closing", {});
}

// #182 — foreground push-suppression: report the PWA's foreground
// visibility to the server over the user-level channel. The server
// (WSPresence) keys it per socket pid and uses the RAW signal to (a)
// suppress foreground Web Push (Push.Triggers) and (b) drive the
// 30s-debounced auto-away FSM (Session.Server). Page-context
// `document.visibilityState` is reliable on iOS PWAs — unlike the SW's
// `clients.matchAll`, which is the root cause of #182.
//
// Fire-and-forget + low frequency (fires on user-channel join and on
// each `visibilitychange`), so no debounce. No-op before `joinUser`.
export function reportVisibility(): void {
  if (_userChannel === null) return;
  if (typeof document === "undefined") return;
  // #192 — presence is visibility AND window focus, not visibility alone.
  // `document.visibilityState` stays "visible" for a desktop tab that is
  // on-screen but has lost keyboard focus (user clicked another app without
  // minimizing/switching tabs), so visibility-only never trips auto-away and
  // #182's per-user any_visible? gate keeps suppressing push on every device.
  // Same predicate as documentVisibility.ts; main.tsx drives this off that
  // module's signal so the blur/focus/visibilitychange listeners are shared.
  const visible = document.visibilityState === "visible" && document.hasFocus();
  _userChannel.push("visibility", { visible }).receive("ok", () => {
    // e2e seam: the last server-acked visibility (mirrors beep.ts's
    // `__lastBeepAt`). Lets a Playwright spec await the WS round-trip
    // deterministically before asserting delivery/suppression.
    if (typeof window !== "undefined") window.__visibilityAck = visible;
  });
}

// S3.4 — /away slash-command pushes.
//
// Both variants push on the user-level channel and return a Promise
// that resolves on "ok" or rejects on "error" (mirrors the `away`
// handle_in reply shape from GrappaChannel). Callers (compose.ts)
// await the promise inside the submit try/catch so errors surface
// as inline compose-box alerts, same as REST failures. Rejects with
// a typed `ChannelPushError` carrying the wire `code` ("no_session",
// "not_explicit", etc.) which compose.ts maps to human copy via
// `friendlyChannelError`.
export function pushAwaySet(network: string, reason: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_userChannel === null) {
      reject(new Error("not connected"));
      return;
    }
    _userChannel
      .push("away", { action: "set", network, reason })
      .receive("ok", () => resolve())
      .receive("error", (err: unknown) => reject(channelPushError(err)));
  });
}

export function pushAwayUnset(network: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_userChannel === null) {
      reject(new Error("not connected"));
      return;
    }
    _userChannel
      .push("away", { action: "unset", network })
      .receive("ok", () => resolve())
      .receive("error", (err: unknown) => reject(channelPushError(err)));
  });
}

// C1.4 — open a DM (query) window. Pushes `open_query_window` on the
// user-level channel; the server upserts the `query_windows` row and
// broadcasts `query_windows_list` back. Fire-and-forget — the
// authoritative update arrives via the broadcast event.
export function pushOpenQueryWindow(networkId: number, targetNick: string): void {
  if (_userChannel === null) return;
  _userChannel.push("open_query_window", { network_id: networkId, target_nick: targetNick });
}

// C1.2 — close a DM (query) window. Pushes `close_query_window` on
// the user-level channel; the server deletes the `query_windows` row
// and broadcasts `query_windows_list` back. Fire-and-forget.
export function pushCloseQueryWindow(networkId: number, targetNick: string): void {
  if (_userChannel === null) return;
  _userChannel.push("close_query_window", { network_id: networkId, target_nick: targetNick });
}

// ---------------------------------------------------------------------------
// S5.3 — Channel ops push helpers. All push on the user-level channel to
// GrappaChannel, which handles all topics (user, network, channel) in the
// same module. Auth is by user_name from socket.assigns; the server
// dispatches to Session.send_*/2-5 functions.
//
// #154(1) — no-silent-drops. State-changing verbs (op/deop/voice/devoice/
// kick/ban/unban/mode/umode) push WITH a `.receive` chain and return a
// Promise, exactly like `pushOper`/`pushRaw`. Pre-fix they were
// fire-and-forget `: void`, so a server `{:error,_}` (visitor_not_allowed
// pre-#153, invalid_channel/nick/mask/line, no_session, upstream_unavailable,
// body_too_large) OR a WS-down was SILENTLY SWALLOWED — compose.ts painted a
// green ✓ on a dropped state-changing frame. `dispatch_subject_verb/3`
// already replies `{:reply, :ok | {:error, %{error: code}}}` for every one
// of these verbs; the compose.ts arms now `await` the Promise so a rejection
// propagates to the shared catch → `friendlyChannelError` inline alert.
//
// `banlist` stays fire-and-forget: it is a read-only query (367/368 numerics
// route back via the numeric pipeline), not a state-changing verb whose error
// must surface inline.
// ---------------------------------------------------------------------------

// Shared promise-shape push for the state-changing channel verbs. Mirrors the
// `pushOper`/`pushRaw` body: resolve on "ok", reject with a typed
// `ChannelPushError` (carrying the wire `code`) on "error", reject "not
// connected" when the socket is down. Single source of the ok/error contract
// so the nine verb helpers below can't drift from each other.
function pushUserChannelVerb(event: string, payload: object): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_userChannel === null) {
      reject(new Error("not connected"));
      return;
    }
    _userChannel
      .push(event, payload)
      .receive("ok", () => resolve())
      .receive("error", (err: unknown) => reject(channelPushError(err)));
  });
}

// /op <nicks...> → MODE #chan +ooo (chunked server-side per ISUPPORT MODES=).
export function pushChannelOp(networkId: number, channel: string, nicks: string[]): Promise<void> {
  return pushUserChannelVerb("op", { network_id: networkId, channel, nicks });
}

// /deop <nicks...> → MODE #chan -ooo
export function pushChannelDeop(
  networkId: number,
  channel: string,
  nicks: string[],
): Promise<void> {
  return pushUserChannelVerb("deop", { network_id: networkId, channel, nicks });
}

// /voice <nicks...> → MODE #chan +vvv
export function pushChannelVoice(
  networkId: number,
  channel: string,
  nicks: string[],
): Promise<void> {
  return pushUserChannelVerb("voice", { network_id: networkId, channel, nicks });
}

// /devoice <nicks...> → MODE #chan -vvv
export function pushChannelDevoice(
  networkId: number,
  channel: string,
  nicks: string[],
): Promise<void> {
  return pushUserChannelVerb("devoice", { network_id: networkId, channel, nicks });
}

// /kick <nick> [reason] → KICK #chan nick :reason
export function pushChannelKick(
  networkId: number,
  channel: string,
  nick: string,
  reason: string,
): Promise<void> {
  return pushUserChannelVerb("kick", { network_id: networkId, channel, nick, reason });
}

// /ban <mask-or-nick> → MODE #chan +b mask (mask derivation server-side if bare nick).
export function pushChannelBan(networkId: number, channel: string, mask: string): Promise<void> {
  return pushUserChannelVerb("ban", { network_id: networkId, channel, mask });
}

// /unban <mask> → MODE #chan -b mask
export function pushChannelUnban(networkId: number, channel: string, mask: string): Promise<void> {
  return pushUserChannelVerb("unban", { network_id: networkId, channel, mask });
}

// /banlist → MODE #chan <mode> (type-A list query form, no sign); the server
// replies with that list's numerics (b 367/368, e 348/349, I 346/347, z|q
// 728/729). Read-only query — stays fire-and-forget (no error to surface
// inline). #1251: `mode` is always sent explicitly; the server's own default
// for an absent field is `b`, which is what a pre-#1251 client relies on.
export function pushChannelBanlist(networkId: number, channel: string, mode: string): void {
  if (_userChannel === null) return;
  _userChannel.push("banlist", { network_id: networkId, channel, mode });
}

// #386 — resolve a nick's userhost on demand. cic has NO per-member host
// (the members map is nick+modes only), so `/kb` and the BanlistModal mask
// builder ask the server for the offender's {user, host} to build `*!*@host`.
// Resolves with the server's cached entry, or `null` on a cache MISS
// (`not_cached` — the fail-closed signal, vjt decision #1, callers turn into
// "run /whois first"). Rejects on a REAL error (no_session, not connected,
// timeout) so the caller can surface it distinctly from a plain miss.
export function resolveUserhost(
  networkId: number,
  nick: string,
): Promise<{ user: string; host: string } | null> {
  const ch = _userChannel;
  if (ch === null) return Promise.reject(new Error("not connected"));
  return new Promise((resolve, reject) => {
    ch.push("resolve_userhost", { network_id: networkId, nick })
      .receive("ok", (reply: { user: string; host: string }) => resolve(reply))
      .receive("error", (err: unknown) => {
        const e = channelPushError(err);
        if (e.code === "not_cached") resolve(null);
        else reject(e);
      })
      .receive("timeout", () => reject(new Error("timeout")));
  });
}

// /invite <nick> [#chan] → INVITE nick #chan
//
// S6 (#364) — no-silent-drop. INVITE is a WRITE verb: the server replies
// invalid_channel/invalid_nick/no_session/upstream_unavailable via
// `dispatch_subject_verb/3`. Pre-fix this was fire-and-forget `: void`, so a
// rejected or WS-down frame was swallowed and compose painted a green ✓.
// Routed through the #154(1) `pushUserChannelVerb` Promise (mirror of
// kick/ban) so compose can `await` and surface the failure inline. banlist
// stays fire-and-forget (its errors surface via the 367/368 numerics).
export function pushChannelInvite(networkId: number, channel: string, nick: string): Promise<void> {
  return pushUserChannelVerb("invite", { network_id: networkId, channel, nick });
}

// /umode <modes> → MODE own_nick <modes> (no channel context required).
export function pushChannelUmode(networkId: number, modes: string): Promise<void> {
  return pushUserChannelVerb("umode", { network_id: networkId, modes });
}

// /mode <target> <modes> [params...] → MODE target modes params (verbatim, no chunking).
export function pushChannelMode(
  networkId: number,
  target: string,
  modes: string,
  params: string[],
): Promise<void> {
  return pushUserChannelVerb("mode", { network_id: networkId, target, modes, params });
}

// /topic -delete → TOPIC #chan : (empty trailing — irssi convention).
// S21: awaited verb-ack (was fire-and-forget). Routes through the same
// `pushUserChannelVerb` Promise as the ops verbs — the server handles
// `topic_clear` via `dispatch_subject_verb/3` (`{:reply, :ok | {:error, _}}`),
// so a WS-down / server rejection now rejects instead of being swallowed.
export function pushChannelTopicClear(networkId: number, channel: string): Promise<void> {
  return pushUserChannelVerb("topic_clear", { network_id: networkId, channel });
}

// Bundle C (#20 follow-up) — /oper <name> <password>. The password
// travels over the WS frame; the bouncer (Session.Server.handle_call
// {:send_oper, ...}) redacts it from any log line by emitting a
// static message body (no interpolation) before sending OPER upstream.
// Server replies arrive as 381 RPL_YOUREOPER (success) or 491 / 464
// (failure) numerics routed via the existing numeric-routing path.
//
// Returns a Promise (not fire-and-forget) so the compose box surfaces
// `invalid_line`, `visitor_not_allowed`, `no_session`, or disconnected
// (`not connected`) failures as inline alerts — credential-bearing
// verbs MUST NOT silently no-op (CLAUDE.md `feedback_no_silent_drops_closed`).
export function pushOper(networkId: number, name: string, password: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_userChannel === null) {
      reject(new Error("not connected"));
      return;
    }
    _userChannel
      .push("oper", { network_id: networkId, name, password })
      .receive("ok", () => resolve())
      .receive("error", (err: unknown) => reject(channelPushError(err)));
  });
}

// Bundle C (#20 follow-up) — /quote <raw IRC line>. Same Promise shape
// as pushOper: a silently-dropped /quote leaves the operator typing into
// the void, so the compose-box error path MUST see WS-disconnected or
// server-side {:error,_} replies.
export function pushRaw(networkId: number, line: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_userChannel === null) {
      reject(new Error("not connected"));
      return;
    }
    _userChannel
      .push("raw", { network_id: networkId, line })
      .receive("ok", () => resolve())
      .receive("error", (err: unknown) => reject(channelPushError(err)));
  });
}

// ---------------------------------------------------------------------------
// S6 (#364) — read-query verbs: no-silent-drop.
//
// whois/whowas/who/names/lusers/info/version/motd were fire-and-forget
// `: void`. Their server handlers reply `{:reply, :ok | {:error, %{error}}}`
// via `dispatch_subject_verb/3` (validation rejects like invalid_nick fire
// BEFORE the upstream write, so NO bundle and NO $server numeric ever arrive
// — a swallowed reject left the operator with literally nothing). All now
// route through the #154(1) `pushUserChannelVerb` Promise so compose can
// `await` and surface the reject inline. Awaiting only confirms the command
// was ACCEPTED (validation + upstream write); the actual reply bundle/modal
// still arrives asynchronously on the user topic, unchanged.
// ---------------------------------------------------------------------------

// #606 — the WHOIS request origin. `"user"` = an operator-issued /whois
// (renders the single-slot scrollback card); `"rail"` = the query-rail
// auto-fetch (feeds the per-nick rail cache, never the scrollback card).
// Add-only wire field: the server treats an absent/unknown value as
// "user". It landed under protocol v1 with no protocol_version bump —
// that was the rule of the day, and #1393d withdrew it on 2026-08-21.
// The server now bumps on EVERY wire-shape change, additive included
// (docs/CLIENT_PROTOCOL.md §2a), so widening this union is not free.
export type WhoisOrigin = "user" | "rail";

// /whois [<server>] <nick> → WHOIS — pushes on the user-level channel.
// `server` is the optional RFC 2812 §3.6.2 target-server (#198): non-null
// for the two-arg `/whois <server> <nick>` form (bouncer emits `WHOIS
// <server> <nick>` upstream), null for the single-arg + bare forms (plain
// `WHOIS <nick>`). Sent explicitly (never omitted) so the server clause
// selection is unambiguous — a null server binary-fails the two-arg
// handle_in guard and falls through to the single-arg clause.
// `origin` (#606) defaults to "user" — the existing /whois call sites
// (compose, UserContextMenu) are unchanged; only the rail passes "rail".
export function pushWhois(
  networkId: number,
  nick: string,
  server: string | null,
  origin: WhoisOrigin = "user",
): Promise<void> {
  return pushUserChannelVerb("whois", { network_id: networkId, nick, server, source: origin });
}

// P-0c — /whowas <nick> → WHOWAS nick — pushes on the user-level
// channel. Server primes whowas_pending + emits WHOWAS upstream;
// 314/312/369/406 fold into the bundle which broadcasts as
// `whowas_bundle` on Topic.user/1. cic dispatches in userTopic.ts
// and the WhowasCard renders inline above the active window.
export function pushWhowas(networkId: number, nick: string): Promise<void> {
  return pushUserChannelVerb("whowas", { network_id: networkId, nick });
}

// #169 — /who <#channel|nick>. Pushes on the user-level channel; server
// primes who_pending + emits WHO upstream. The 352 burst folds server-side
// (each also upserting userhost_cache) and 315 RPL_ENDOFWHO drains into ONE
// ephemeral `who_reply` event on the user topic (WhoModal renders it) — NOT
// persisted to scrollback. Mirrors `pushNames`.
export function pushWho(networkId: number, channel: string): Promise<void> {
  return pushUserChannelVerb("who", { network_id: networkId, channel });
}

// P-0d — /lusers [<mask> [<server>]] → LUSERS upstream. Pushes on
// the user-level channel; server emits the 7-numeric bundle which
// EventRouter folds and 266 RPL_GLOBALUSERS flushes into a typed
// :lusers_bundle wire event on Topic.user/1. cic dispatches in
// userTopic.ts and renders the LusersCard in the current window (#231).
// #579 — both RFC 2812 §3.4.2 args thread through; a null one OMITS its
// wire key (pushMotd/pushLinks' optional-arg shape) so grappa's
// `validate_lusers_args/2` sees the same absent-arg shape a pre-#579
// client sends. `server` needs `mask`: the parser cannot produce one
// without the other, and grappa refuses that shape as `:invalid_line`.
export function pushLusers(
  networkId: number,
  mask: string | null,
  server: string | null,
): Promise<void> {
  return pushUserChannelVerb("lusers", {
    network_id: networkId,
    ...(mask === null ? {} : { mask }),
    ...(server === null ? {} : { server }),
  });
}

// #127 — /info, /version, /motd. Push on the user-level channel; server
// primes the matching pending accumulator + emits the bare command upstream.
// The reply burst drains ONE ephemeral `server_reply` event on Topic.user/1
// which userTopic.ts routes into the serverReplyModal store (ServerReplyModal
// renders it). Connect-time MOTD is unaffected (no pending flag → $server).
export function pushInfo(networkId: number): Promise<void> {
  return pushUserChannelVerb("info", { network_id: networkId });
}

export function pushVersion(networkId: number): Promise<void> {
  return pushUserChannelVerb("version", { network_id: networkId });
}

// #374 — /motd [<target>]. Bare (target null) omits the wire key so grappa
// emits `MOTD`; a target sends `{ target }` so grappa emits `MOTD <target>`
// and the 375/372/376 (or 402 ERR_NOSUCHSERVER) reply routes back through
// the same server_reply modal.
export function pushMotd(networkId: number, target: string | null): Promise<void> {
  return pushUserChannelVerb(
    "motd",
    target === null ? { network_id: networkId } : { network_id: networkId, target },
  );
}

// #992 — /admin [<target>]. Identical optional-arg shape to pushMotd: bare
// omits the wire key so grappa emits `ADMIN`, a target sends `{ target }` so
// grappa emits `ADMIN <target>`. The 256/257/258/259 burst (or 423
// ERR_NOADMININFO / 402 ERR_NOSUCHSERVER / 447 ERR_RESTRICTED) routes back
// through the same server_reply modal.
export function pushAdmin(networkId: number, target: string | null): Promise<void> {
  return pushUserChannelVerb(
    "admin",
    target === null ? { network_id: networkId } : { network_id: networkId, target },
  );
}

// #238 — /links [<mask>]. Bare (mask null) omits the wire key so grappa emits
// `LINKS`; a mask sends `{ mask }` so grappa emits `LINKS <mask>`. The 364/365
// burst drains ONE ephemeral `links_bundle` event on Topic.user/1 which
// userTopic.ts routes into the linksModal store (LinksModal renders the
// interactive topology map). Mirrors pushMotd's optional-arg omit.
export function pushLinks(networkId: number, mask: string | null): Promise<void> {
  return pushUserChannelVerb(
    "links",
    mask === null ? { network_id: networkId } : { network_id: networkId, mask },
  );
}

// #581 — /recover. Push on the user-level channel; the server runs the guided
// NickServ recovery sequence and streams `recover_progress` / `recover_result`
// events back on the user topic (RecoverModal mirrors them). The Promise
// resolves once the verb is ACCEPTED (validation + kickoff); the modal opens
// off the first `recover_progress`, NOT this resolve — so the arm does NOT open
// optimistically. A server rejection (`nothing_to_recover` / `already_identified`
// / `not_visitor`, or a WS-down) rejects with a typed `ChannelPushError` the
// compose arm maps to friendly copy. Mirror of pushLinks/pushLusers.
export function pushRecover(networkId: number): Promise<void> {
  return pushUserChannelVerb("recover", { network_id: networkId });
}

// #140 — /names <#channel>. Pushes on the user-level channel; server
// primes names_pending + emits NAMES upstream. The 353/366 burst drains
// into ONE ephemeral `names_reply` event on the user topic (NamesModal
// renders it) — NOT persisted. Network-scoped modal, so no origin window.
export function pushNames(networkId: number, channel: string): Promise<void> {
  return pushUserChannelVerb("names", { network_id: networkId, channel });
}

// C8.3 — Watchlist verbs (/watch /highlight). All push on the user-level
// channel; server-side GrappaChannel.handle_in("watchlist", ...) handlers
// are the authority (added in C8 server-side commit). The Promise resolves
// with {patterns: string[]} on success, rejects on server error or timeout.
//
// Pattern semantics: forward-only (changing the list does NOT re-aggregate
// past scrollback; only future mentions are filtered by the new list).
export function pushWatchlistAdd(pattern: string): Promise<{ patterns: string[] }> {
  const ch = _userChannel;
  if (ch === null) return Promise.reject(new Error("not connected"));
  return new Promise((resolve, reject) => {
    ch.push("watchlist", { action: "add", pattern })
      .receive("ok", (reply: { patterns: string[] }) => resolve(reply))
      .receive("error", (err: unknown) => reject(channelPushError(err)))
      .receive("timeout", () => reject(new Error("timeout")));
  });
}

export function pushWatchlistDel(pattern: string): Promise<{ patterns: string[] }> {
  const ch = _userChannel;
  if (ch === null) return Promise.reject(new Error("not connected"));
  return new Promise((resolve, reject) => {
    ch.push("watchlist", { action: "del", pattern })
      .receive("ok", (reply: { patterns: string[] }) => resolve(reply))
      .receive("error", (err: unknown) => reject(channelPushError(err)))
      .receive("timeout", () => reject(new Error("timeout")));
  });
}

export function pushWatchlistList(): Promise<{ patterns: string[] }> {
  const ch = _userChannel;
  if (ch === null) return Promise.reject(new Error("not connected"));
  return new Promise((resolve, reject) => {
    ch.push("watchlist", { action: "list", pattern: undefined })
      .receive("ok", (reply: { patterns: string[] }) => resolve(reply))
      .receive("error", (err: unknown) => reject(channelPushError(err)))
      .receive("timeout", () => reject(new Error("timeout")));
  });
}

// E2E hooks (message-replay-on-reconnect cluster, 2026-05-12) — drop the
// live socket, then reconnect on an EXPLICIT resume, so Playwright can
// simulate the tab-suspend / network-blip / iOS-Safari-tab-resume gap class
// without juggling browser-context offline mode (which closes ALL
// connections including the REST fetches the test depends on).
//
// Two-phase (drop → resume) so the "socket is down" window is
// DETERMINISTIC. `Socket.disconnect()` is an EXPLICIT close: phoenix.js
// resets its internal reconnectTimer, so — unlike an unexpected drop — the
// socket does NOT auto-retry. It stays non-open until the test calls
// `__cic_resumeSocketForTests()`. This is the same halt-and-hold phoenix
// behaviour `haltForOffline` relies on.
//
// The prior single-call variant (disconnect → microtask → connect)
// RACED: phoenix reopened so fast that `socketHealth.state` flipped back to
// "open" before a Playwright `waitForFunction(state !== "open")` could
// catch the transient non-open window — a flaky WS-gap premise that timed
// out ~40% under load, on both the 1.19 and 1.20 toolchains (GH #186, found
// during the #53 migration validation). Holding the socket down makes the
// non-open state STABLE, so the gate observes it every time.
//
// On resume, `connect()` re-evaluates `params()` and phoenix auto-rejoins
// every joined Channel; each per-channel rejoin's onJoinOk fires the
// reconnect-backfill flow, so the gap-recovery path is exercised end-to-end
// exactly as before.
declare global {
  interface Window {
    __cic_dropSocketForTests?: () => Promise<void>;
    __cic_resumeSocketForTests?: () => Promise<void>;
    // #182 e2e seam — last visibility the server acked (see reportVisibility).
    __visibilityAck?: boolean;
  }
}

if (typeof window !== "undefined") {
  // Drop and HOLD — resolve only once the WS close has actually landed
  // (disconnect's completion callback fires after teardown), so the caller
  // can immediately assert a stable non-open `socketHealth.state`.
  window.__cic_dropSocketForTests = async () => {
    const s = _socket;
    if (!s) return;
    await new Promise<void>((resolve) => s.disconnect(() => resolve()));
  };
  // Resume the held socket — phoenix reconnects and auto-rejoins every
  // channel, driving the reconnect-backfill recovery path.
  //
  // #1061 — deliberately NOT routed through `connectUnlessOffline`. This is an
  // explicit test verb ("resume now"), not a production open, and a spec that
  // stubs `navigator.onLine` to drive some other scenario must still be able
  // to bring the socket back. Production has no path here.
  window.__cic_resumeSocketForTests = async () => {
    const s = _socket;
    if (!s) return;
    s.connect();
  };
}
